import { cloneDeep, isNotNil, partition } from 'es-toolkit';
import { get } from 'es-toolkit/compat';
import { arrayPathToFormPath } from './path-conversion';

/**
 * Array wildcard character used in structure paths to indicate per-item propagation.
 * Example: 'carousel.items.$.link' - copies link field for each array item individually.
 */
const ARRAY_WILDCARD = '$';

/**
 * Extracts the array path from a wildcard path.
 *
 * Returns everything before the first wildcard character ($).
 *
 * @param path - Wildcard path to parse
 * @returns The array path (everything before the first $)
 *
 * @example
 * parseWildcardPath('carousel.items.$.link')
 * // Returns: 'carousel.items'
 *
 * @example
 * parseWildcardPath('sections.$.blocks.$.link')
 * // Returns: 'sections'
 */
function parseWildcardPath(path: PropertyKey[]) {
  return path.slice(0, path.indexOf(ARRAY_WILDCARD));
}

/**
 * Extracts and clones a value from source data for a given path.
 * Returns undefined for null/undefined values to support field removal during structure changes.
 *
 * @param sourceData - Form data object to read from
 * @param path - Dot-notation path to the value
 * @returns Cloned value or undefined
 */
function extractAndCloneValue(sourceData: unknown, path: PropertyKey[]): unknown {
  const value = get(sourceData, path);
  // isNotNil check is intentionally removed - we want to propagate undefined
  // to remove obsolete fields when structure changes
  if (isNotNil(value)) return cloneDeep(value);
}

/**
 * Processes structure paths (with or without wildcards) for propagation.
 *
 * This function:
 * 1. Detects wildcards in paths and processes accordingly
 * 2. For wildcard paths: Extracts source array and processes each item
 * 3. For regular paths: Directly extracts and clones the value
 * 4. Generates form change objects for target flavor
 *
 * Undefined values naturally remove obsolete fields when structure changes.
 * For example, switching from 'drawer' to 'link' action type will set
 * drawerContent to undefined, removing it from the target flavor.
 *
 * @param params - Configuration object
 * @param params.structurePaths - Array of paths (with or without wildcard syntax)
 * @param params.sourceData - Form data object to read values from
 * @param params.targetFlavorIndex - Index of target flavor to update
 * @returns Array of form changes ready for batch update
 *
 * @example
 * // With wildcards
 * processStructurePaths({
 *   structurePaths: ['carousel.items.$.link', 'carousel.items.$.drawerContent'],
 *   sourceData: {
 *     carousel: {
 *       items: [
 *         { link: { text: 'A', href: 'a' }, drawerContent: undefined },
 *         { link: undefined, drawerContent: { sections: [] } },
 *       ],
 *     },
 *   },
 *   targetFlavorIndex: 1,
 * })
 *
 * @example
 * // Without wildcards
 * processStructurePaths({
 *   structurePaths: ['arguments.carousel.carouselItems'],
 *   sourceData: { arguments: { carousel: { carouselItems: [...] } } },
 *   targetFlavorIndex: 1,
 * })
 */
export function processStructurePaths({
  structurePaths,
  sourceData,
  targetFlavorIndex,
}: {
  structurePaths: PropertyKey[][];
  sourceData: unknown;
  targetFlavorIndex: number;
}) {
  const formChanges = [];

  // Group paths by whether they contain wildcards
  const [wildcardPaths, regularPaths] = partition(structurePaths, (path) => path.includes(ARRAY_WILDCARD));

  // Process wildcard paths (per-item propagation)
  if (wildcardPaths.length > 0) {
    const arrayPath = parseWildcardPath(wildcardPaths[0]);
    const sourceArray = get(sourceData, arrayPath);
    if (!Array.isArray(sourceArray)) return [];

    // Process each array item
    formChanges.push(
      ...sourceArray.flatMap((_, i) =>
        wildcardPaths
          .map((wildcardPath) => {
            const resolvedPath = [
              ...wildcardPath,
            ];
            resolvedPath[resolvedPath.indexOf(ARRAY_WILDCARD)] = i;
            return resolvedPath;
          })
          .map((resolvedPath) => ({
            path: `flavors[${targetFlavorIndex}].content${arrayPathToFormPath(resolvedPath)}`,
            value: extractAndCloneValue(sourceData, resolvedPath),
          })),
      ),
    );
  }

  // Process regular paths (entire value propagation)
  formChanges.push(
    ...regularPaths
      .filter((path) => isNotNil(get(sourceData, path)))
      .map((regularPath) => ({
        path: `flavors[${targetFlavorIndex}].content${arrayPathToFormPath(regularPath)}`,
        value: extractAndCloneValue(sourceData, regularPath),
      })),
  );

  return formChanges;
}
